/**
 * search — THE frontend Search store (ADR-0017).
 *
 * One module owns the active Search's state as two named parts:
 *
 *   criteria — what the server searches (the filter panel, ADR-0001).
 *              sessionStorage; survives refresh within the tab.
 *   lens     — the client-side narrowing that hides map pins WITHOUT
 *              re-searching (see CONTEXT.md "Lens"). sessionStorage for
 *              the session. Per-surface policy is part of the concept:
 *              the vibe map's Lens starts EMPTY (the AI may deliberately
 *              show a building the Lens would hide — the map must never
 *              contradict the conversation); the browse map seeds the
 *              modern-stock default ONCE per Search and a cleared Lens
 *              stays cleared.
 *
 * Interface tiers (ADR-0017):
 *   - ordinary knobs: setFilter / setFilters / patchLens etc. — plain merges;
 *   - the cross-surface TRANSITIONS are named verbs implemented ONLY here:
 *     submitSearch, applyVibeAction, enterBrowse, inheritToLens, newSearch.
 *     Pages must not hand-roll them.
 *
 * Cross-store effects (projected-count refresh, quiz invalidation) are
 * REGISTERED by apartmentStore at module init — same registry pattern as
 * searchSession — so this module never imports apartmentStore and the old
 * circular-import dance stays dead.
 */
import { create } from 'zustand';
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware';
import type { FiltersState, WorkHub } from '../types/stores';
import type { Apartment } from '../types/apartment';
import { safeLocal, safeSessionStorage } from '@/utils/safeStorage';
import { useMetroStore } from './metroStore';
import {
  DEFAULT_MAP_FILTERS,
  EMPTY_MAP_FILTERS,
  filterApartments,
  mapFiltersFromCriteria,
  type MapFilters,
} from './searchCriteria';
import { registerSearchReset } from './searchSession';

// ── Registered cross-store effects ──────────────────────────────────
// apartmentStore registers these at module init. Per-action wiring is
// preserved exactly: only the mutations that refreshed the projected
// count before the extraction call onCountRefetch.

type Effect = () => void;
let onCountRefetch: Effect = () => {};

export function registerCountRefetch(fn: Effect): void {
  onCountRefetch = fn;
}

const getInitialCity = (): string => {
  try {
    const stored = JSON.parse(safeLocal.get('taco-metro') || '{}');
    return stored?.state?.selectedMetro || 'Austin';
  } catch {
    return 'Austin';
  }
};

/** The old wizard default. Session snapshots that still carry both ends
 *  never had a renter-chosen budget, so they drop back to "no budget". */
export function withoutRetiredBudgetDefault<T extends { maxBudget?: number | null; minBudget?: number | null }>(filters: T): T {
  if (filters.maxBudget === 3000 && filters.minBudget === 1500) {
    return { ...filters, maxBudget: null, minBudget: null };
  }
  return filters;
}

export const initialCriteria: FiltersState = {
  city: getInitialCity(),
  // Step 1
  researchStage: null,
  movingFrom: null,
  needsNeighborhoodHelp: false,
  lifestyleProfiles: [],
  vibeWords: [],
  workLocationMode: null,
  workLocationText: '',
  workLocationCoords: null,
  maxCommute: null,
  neighborhoods: [],
  moveInTimeline: null,
  customMoveDate: '',
  bedrooms: [],
  // No budget until the renter sets one. A prefilled $1,500–$3,000 range
  // was applied to every new search.
  maxBudget: null,
  minBudget: null,
  budgetFlex: 'some',
  leaseTerm: null,
  // Step 2
  buildingAges: [],
  standardAmenities: [],
  minRating: null,
  minSqft: undefined,
  // Step 3
  whyAustinList: [],
  noGroundFloor: false,
  noStreetFacing: false,
  mustHaveCoveredParking: false,
  noCarpet: false,
  mustAllowLargeDogs: false,
  hasSpecial: false,
  seniorHousing: false,
  studentHousing: false,
  homeTypes: [],
  hasGarage: false,
  hasYard: false,
  aiCurated: true,
  // Step 4
  petOptions: [],
  petFriendly: false,
  roadblocks: [],
  naturalLanguage: '',
  email: '',
};

export interface SearchStoreState {
  // ── criteria (the panel; ADR-0001) ──
  filters: FiltersState;

  // ── the Lens (client-side narrowing; CONTEXT.md) ──
  lens: MapFilters;
  /** The "filters carried over" banner is armed (shown once). */
  inheritedPending: boolean;
  /** Browse has seeded its modern-stock default lens this Search. */
  browseSeeded: boolean;

  // ── ordinary knobs: criteria ──
  syncMetro: () => void;
  setFilter: (key: string, value: unknown) => void;
  setFilters: (newFilters: Partial<FiltersState>) => void;
  /** Wholesale replacement (quick-search presets build from initialCriteria). */
  replaceFilters: (next: FiltersState) => void;
  setFiltersFromVibe: (criteria: Record<string, unknown>) => void;
  toggleFilter: (key: string, value: string) => void;
  toggleBuildingAge: (age: string) => void;
  togglePetOption: (pet: string) => void;
  toggleSpecificPref: (pref: string) => void;
  toggleNeedHelp: () => void;
  skipQuiz: () => void;
  toggleNeighborhood: (regionName: string) => void;
  clearNeighborhoods: () => void;
  toggleLifestyleProfile: (profileId: string) => void;
  selectWorkHub: (hub: WorkHub) => void;
  resetFilters: () => void;

  // ── ordinary knobs: lens ──
  setLens: (next: MapFilters) => void;
  patchLens: (patch: Partial<MapFilters>) => void;
  resetLens: () => void;
  acknowledgeInherited: () => void;

  // ── the transitions (ADR-0017 — only this store implements them) ──
  /** Merge a partial onto the Lens and arm the one-time banner (the
   *  primitive under inheritToLens; also used by tests). */
  inheritFromSearch: (partial: Partial<MapFilters>) => void;
  /** Criteria Search submitted: carry the panel onto the Lens and arm the
   *  one-time "filters carried over" banner. */
  inheritToLens: () => void;
  /** Vibe input adapter: merge an extracted snake_case criteria dict (or a
   *  camelCase patch from a pill action) into the panel. */
  applyVibeAction: (patch: Record<string, unknown>) => void;
  /** Browse-page bootstrap: seed the modern-stock default lens ONCE per
   *  Search (visible, removable chips — a cleared Lens stays cleared). */
  enterBrowse: () => void;
  /** Run the Search from the panel (delegates to apartmentStore's engine
   *  adapter, which owns results). */
  submitSearch: () => Promise<void>;
}

export const useSearchStore = create<SearchStoreState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        filters: { ...initialCriteria },

        // The Lens starts EMPTY: the vibe map must never narrow the
        // conversation's curated results by default. Browse opts into the
        // modern-stock lens via enterBrowse() on its own bootstrap.
        lens: { ...EMPTY_MAP_FILTERS },
        inheritedPending: false,
        browseSeeded: false,

        // ── criteria knobs (moved verbatim from apartmentStore) ──
        syncMetro: () => {
          const metro = useMetroStore.getState().selectedMetro;
          if (metro && metro !== get().filters.city) {
            set((state) => ({ filters: { ...state.filters, city: metro } }));
          }
        },

        setFilter: (key, value) => {
          set((state) => ({ filters: { ...state.filters, [key]: value } }));
          onCountRefetch();
        },

        setFilters: (newFilters) => {
          set((state) => ({ filters: { ...state.filters, ...newFilters } }));
          onCountRefetch();
        },

        replaceFilters: (next) => {
          set({ filters: { ...next } });
          onCountRefetch();
        },

        // ADR-0001 — Vibe input is one of three input modes for a Search;
        // the filter panel is the source of truth. Translates the snake_case
        // criteria dict from vibe extraction into FiltersState and merges.
        setFiltersFromVibe: (criteria) => {
          const patch: Partial<FiltersState> = {};

          if (Array.isArray(criteria.bedrooms) && criteria.bedrooms.length > 0) {
            patch.bedrooms = criteria.bedrooms as string[];
          }
          // Budget — a lone figure is the ceiling it reads as, with no
          // invented floor (see the panel-chips postmortem in git history).
          const bMax = typeof criteria.budget_max === 'number' && criteria.budget_max > 0 ? criteria.budget_max : null;
          const bMin = typeof criteria.budget_min === 'number' && criteria.budget_min > 0 ? criteria.budget_min : null;
          if (criteria.budget_max === null) {
            patch.maxBudget = null;
            if (bMin) patch.minBudget = bMin;
            else if (criteria.budget_min === null) patch.minBudget = null;
          } else if (bMax && bMin) {
            patch.minBudget = bMin;
            patch.maxBudget = bMax;
          } else if (bMax) {
            patch.maxBudget = bMax;
            patch.minBudget = null;
          } else if (bMin) {
            patch.minBudget = bMin;
          }
          if (Array.isArray(criteria.neighborhoods) && criteria.neighborhoods.length > 0) {
            patch.neighborhoods = criteria.neighborhoods as string[];
          }
          const petList = [
            ...(Array.isArray(criteria.pets) ? criteria.pets : []),
            ...(Array.isArray(criteria.pet_types) ? criteria.pet_types : []),
          ].map((pet) => String(pet));
          if (criteria.pet_friendly === true || petList.length > 0) {
            patch.petFriendly = true;
          }
          if (Array.isArray(criteria.home_types) && criteria.home_types.length > 0) {
            patch.homeTypes = criteria.home_types as string[];
          }
          const species = petList
            .map((pet) => pet.toLowerCase())
            .filter((pet) => pet === 'dog' || pet === 'cat');
          if (typeof criteria.pet_type === 'string' && criteria.pet_type) {
            patch.petOptions = criteria.pet_type === 'both' ? ['dog', 'cat'] : [criteria.pet_type];
          } else if (species.length > 0) {
            patch.petOptions = Array.from(new Set(species));
          }
          const vibes = (criteria.vibe_words ?? criteria.vibes) as unknown;
          if (Array.isArray(vibes) && vibes.length > 0) {
            patch.vibeWords = vibes as string[];
          }
          if (Array.isArray(criteria.lifestyle_profiles) && criteria.lifestyle_profiles.length > 0) {
            patch.lifestyleProfiles = criteria.lifestyle_profiles as string[];
          }
          if (Array.isArray(criteria.amenities) && criteria.amenities.length > 0) {
            patch.standardAmenities = criteria.amenities as string[];
          }
          if (typeof criteria.min_sqft === 'number' && criteria.min_sqft > 0) {
            patch.minSqft = criteria.min_sqft;
          }
          if (typeof criteria.min_rating === 'number' && criteria.min_rating > 0) {
            patch.minRating = criteria.min_rating;
          }
          if (Array.isArray(criteria.building_ages) && criteria.building_ages.length > 0) {
            patch.buildingAges = criteria.building_ages as string[];
          }

          const commute = criteria.commute as Record<string, unknown> | undefined;
          if (commute && typeof commute === 'object') {
            if (typeof commute.work_address === 'string' && commute.work_address) {
              patch.workLocationText = commute.work_address;
            }
            if (typeof commute.max_commute_minutes === 'number' && commute.max_commute_minutes > 0) {
              patch.maxCommute = commute.max_commute_minutes;
            }
          }

          if (Object.keys(patch).length === 0) return;

          set((state) => ({ filters: { ...state.filters, ...patch } }));
          onCountRefetch();
        },

        toggleFilter: (key, value) => {
          set((state) => {
            const current = (state.filters[key] as string[]) || [];
            const updated = current.includes(value)
              ? current.filter((v: string) => v !== value)
              : [...current, value];
            return { filters: { ...state.filters, [key]: updated } };
          });
          onCountRefetch();
        },

        toggleBuildingAge: (age) => {
          set((state) => {
            let ages = [...(state.filters.buildingAges || [])];
            if (age === 'any') {
              ages = ['any'];
            } else {
              ages = ages.filter(a => a !== 'any');
              const idx = ages.indexOf(age);
              if (idx > -1) ages.splice(idx, 1);
              else ages.push(age);
            }
            return { filters: { ...state.filters, buildingAges: ages } };
          });
          onCountRefetch();
        },

        togglePetOption: (pet) => {
          set((state) => {
            let opts = [...(state.filters.petOptions || [])];
            if (pet === 'none') {
              opts = ['none'];
              return { filters: { ...state.filters, petOptions: opts, petFriendly: false } };
            } else {
              opts = opts.filter(p => p !== 'none');
              const idx = opts.indexOf(pet);
              if (idx > -1) opts.splice(idx, 1);
              else opts.push(pet);
              return { filters: { ...state.filters, petOptions: opts, petFriendly: opts.length > 0 } };
            }
          });
          onCountRefetch();
        },

        toggleSpecificPref: (pref) => {
          set((state) => ({
            filters: { ...state.filters, [pref]: !state.filters[pref] }
          }));
          onCountRefetch();
        },

        toggleNeedHelp: () => {
          set((state) => {
            const needsHelp = !state.filters.needsNeighborhoodHelp;
            const updates: Partial<FiltersState> = { needsNeighborhoodHelp: needsHelp };
            if (needsHelp) updates.neighborhoods = [];
            return { filters: { ...state.filters, ...updates } };
          });
        },

        skipQuiz: () => {
          set((state) => ({
            filters: { ...state.filters, needsNeighborhoodHelp: false },
          }));
        },

        toggleNeighborhood: (regionName) => {
          set((state) => {
            const hoods = [...(state.filters.neighborhoods || [])];
            const idx = hoods.indexOf(regionName);
            if (idx > -1) hoods.splice(idx, 1);
            else hoods.push(regionName);
            return { filters: { ...state.filters, neighborhoods: hoods } };
          });
          onCountRefetch();
        },

        clearNeighborhoods: () => {
          set((state) => ({ filters: { ...state.filters, neighborhoods: [] } }));
          onCountRefetch();
        },

        toggleLifestyleProfile: (profileId) => {
          set((state) => {
            const profiles = [...(state.filters.lifestyleProfiles || [])];
            const idx = profiles.indexOf(profileId);
            if (idx > -1) profiles.splice(idx, 1);
            else profiles.push(profileId);
            const updates: Partial<FiltersState> = { lifestyleProfiles: profiles };
            if (profileId === 'senior_living') {
              updates.seniorHousing = profiles.includes('senior_living');
            }
            if (profileId === 'student_living') {
              updates.studentHousing = profiles.includes('student_living');
            }
            return { filters: { ...state.filters, ...updates } };
          });
        },

        selectWorkHub: (hub) => {
          set((state) => ({
            filters: {
              ...state.filters,
              workLocationCoords: { hubId: hub.id, name: hub.name, lat: hub.lat, lng: hub.lng, fromHub: true },
              workLocationText: hub.name,
            }
          }));
        },

        resetFilters: () => {
          const city = useMetroStore.getState().selectedMetro || initialCriteria.city;
          set({ filters: { ...initialCriteria, city } });
          onCountRefetch();
        },

        // ── lens knobs (moved verbatim from mapFilterStore) ──
        setLens: (next) => set({ lens: next, inheritedPending: false }),

        patchLens: (patch) =>
          set((s) => ({ lens: { ...s.lens, ...patch }, inheritedPending: false })),

        resetLens: () => set({ lens: { ...EMPTY_MAP_FILTERS }, inheritedPending: false }),

        acknowledgeInherited: () => set({ inheritedPending: false }),

        // ── transitions ──
        inheritFromSearch: (partial) => {
          set((s) => ({
            lens: { ...s.lens, ...partial },
            inheritedPending: true,
          }));
        },

        inheritToLens: () => {
          get().inheritFromSearch(mapFiltersFromCriteria(get().filters));
        },

        applyVibeAction: (patch) => {
          // Pill actions arrive camelCase (a FiltersState patch); extraction
          // results arrive snake_case. setFiltersFromVibe handles snake_case;
          // camelCase merges directly. Heuristic: snake_case keys contain '_'.
          const keys = Object.keys(patch || {});
          if (keys.length === 0) return;
          const isSnake = keys.some((k) => k.includes('_'));
          if (isSnake) get().setFiltersFromVibe(patch);
          else get().setFilters(patch as Partial<FiltersState>);
        },

        enterBrowse: () => {
          const s = get();
          if (s.browseSeeded) return;
          set({ lens: { ...DEFAULT_MAP_FILTERS }, browseSeeded: true, inheritedPending: false });
        },

        submitSearch: async () => {
          const { useApartmentStore } = await import('./apartmentStore');
          await useApartmentStore.getState().performSearch(0);
        },
      }),
      {
        name: 'taco-search-lens',
        storage: createJSONStorage(() => safeSessionStorage),
        // Persist the current criteria, including edits/cleared selections,
        // rather than replaying an older AI extraction over the filter panel.
        // New Search resets these through the registry below.
        partialize: (s) => ({
          filters: s.filters,
          lens: s.lens,
          inheritedPending: s.inheritedPending,
          browseSeeded: s.browseSeeded,
        }),
        merge: (persisted, current) => {
          const saved = persisted as Partial<SearchStoreState> | undefined;
          const filters = saved?.filters;
          return {
            ...current,
            ...saved,
            // A metro selected in another tab must not revive this tab's
            // old city criteria. Legacy lens-only snapshots use defaults.
            filters: filters?.city === current.filters.city
              ? withoutRetiredBudgetDefault({ ...current.filters, ...filters })
              : current.filters,
          };
        },
      }
    )
  )
);

// "+ New Search" wipes the transient Search everywhere (searchSession).
registerSearchReset('searchStore', () => {
  const city = useMetroStore.getState().selectedMetro || initialCriteria.city;
  useSearchStore.setState({
    filters: { ...initialCriteria, city },
    lens: { ...EMPTY_MAP_FILTERS },
    inheritedPending: false,
    browseSeeded: false,
  });
});

/** Apply the Lens to a result list — the ONE place pin-hiding is computed. */
export function narrowApartments(apartments: Apartment[], lens: MapFilters): Apartment[] {
  return filterApartments(apartments, lens);
}

/** Hook: the given apartments narrowed by the current Lens. */
export function useNarrowedApartments(apartments: Apartment[]): Apartment[] {
  const lens = useSearchStore((s) => s.lens);
  return narrowApartments(apartments, lens);
}

/** Resume a conversation in a fresh tab without replacing that tab's own edits.
 * The Lens remains tab-local; only the conversation's criteria are restored.
 */
export function restoreConversationInNewTab(
  city: string,
  filters?: ReturnType<typeof useSearchStore.getState>['filters'],
  criteria?: Record<string, unknown> | null,
): void {
  const current = useSearchStore.getState();
  if (city !== current.filters.city) return;
  try {
    const saved = JSON.parse(safeSessionStorage.getItem('taco-search-lens') || 'null');
    if (saved?.state?.filters) return;
  } catch { /* Corrupt storage has no usable tab criteria. */ }
  if (filters?.city === city) current.setFilters(filters);
  else if (criteria) current.applyVibeAction(criteria);
}
